import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import { Avatar, AvatarFallback } from './ui/avatar';
import { avatarTint, iniciales, dinero } from '../lib/identity';
import { Phone, Plus, Search, UserPlus, Wallet } from 'lucide-react';

import { API } from '@/lib/api';

/**
 * Paleta de comandos (⌘K / Ctrl+K).
 *
 * Resuelve el problema de "¿dónde estaba eso?": navegación completa + acciones
 * frecuentes + búsqueda de pacientes por nombre o teléfono, todo desde el
 * teclado y sin cambiar de pantalla. Los resultados de pacientes se buscan
 * contra la API con debounce (250 ms) para no disparar una petición por tecla.
 */
export const CommandPalette = ({ open, onOpenChange, navItems = [], acciones = [] }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [pacientes, setPacientes] = useState([]);
  const [buscando, setBuscando] = useState(false);

  // Atajo global: ⌘K en Mac, Ctrl+K en Windows/Linux. "/" también abre.
  useEffect(() => {
    const onKeyDown = (e) => {
      const esAtajo = e.key?.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey);
      if (esAtajo) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) { setQuery(''); setPacientes([]); }
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (!open || term.length < 2) { setPacientes([]); return undefined; }

    setBuscando(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/patients`, {
          params: { search: term, limit: 6, solo_clientes: false },
        });
        setPacientes(Array.isArray(data) ? data : []);
      } catch {
        setPacientes([]);
      } finally {
        setBuscando(false);
      }
    }, 250);

    return () => clearTimeout(id);
  }, [query, open]);

  const grupos = useMemo(() => ({ navItems, acciones }), [navItems, acciones]);

  const ir = (fn) => { onOpenChange(false); fn(); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={query.trim().length < 2}>
      <CommandInput
        placeholder="Buscar pacientes, ir a una pantalla o ejecutar una acción…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[420px]">
        <CommandEmpty>
          {buscando ? 'Buscando…' : 'Sin resultados. Prueba con el nombre o teléfono del paciente.'}
        </CommandEmpty>

        {pacientes.length > 0 && (
          <>
            <CommandGroup heading="Pacientes">
              {pacientes.map((p) => {
                const nombre = `${p.nombre} ${p.apellido}`;
                return (
                  <CommandItem
                    key={p.id}
                    value={`paciente-${p.id}-${nombre}`}
                    onSelect={() => ir(() => navigate(`/patients/${p.id}`))}
                    className="gap-3"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-[10px] font-semibold text-white" style={avatarTint(nombre)}>
                        {iniciales(nombre)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{nombre}</p>
                      <p className="truncate text-xs text-muted-foreground">{p.telefono || 'Sin teléfono'}</p>
                    </div>
                    {p.tiene_plan && (
                      <span className="rounded-full bg-success/10 px-2 py-0.5 text-[10px] font-semibold text-success">
                        Con plan
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {grupos.acciones.length > 0 && (
          <>
            <CommandGroup heading="Acciones rápidas">
              {grupos.acciones.map((a) => (
                <CommandItem key={a.label} value={`accion-${a.label}`} onSelect={() => ir(a.run)}>
                  <a.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{a.label}</span>
                  {a.hint && <CommandShortcut>{a.hint}</CommandShortcut>}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Ir a">
          {grupos.navItems.map((item) => (
            <CommandItem
              key={item.path}
              value={`nav-${item.label}`}
              onSelect={() => ir(() => navigate(item.path))}
            >
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>

      <div className="flex items-center justify-between border-t border-border/70 bg-surface/60 px-3 py-2 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1"><kbd className="rounded border border-border bg-card px-1">↑↓</kbd> navegar</span>
          <span className="flex items-center gap-1"><kbd className="rounded border border-border bg-card px-1">↵</kbd> abrir</span>
        </span>
        <span className="flex items-center gap-1"><kbd className="rounded border border-border bg-card px-1">esc</kbd> cerrar</span>
      </div>
    </CommandDialog>
  );
};

/** Acciones por defecto del sistema (se filtran por rol en Layout). */
export const accionesPorRol = (rol) => {
  const base = [
    {
      label: 'Nueva cita',
      icon: Plus,
      hint: 'C',
      roles: ['admin', 'recepcion', 'doctor'],
      run: () => window.dispatchEvent(new CustomEvent('dentu:accion', { detail: { accion: 'nueva-cita' } })),
    },
    {
      label: 'Nuevo paciente',
      icon: UserPlus,
      hint: 'P',
      roles: ['admin', 'recepcion', 'doctor'],
      run: () => window.dispatchEvent(new CustomEvent('dentu:accion', { detail: { accion: 'nuevo-paciente' } })),
    },
    {
      label: 'Registrar cobro de una cita',
      icon: Wallet,
      roles: ['admin', 'recepcion'],
      run: () => window.dispatchEvent(new CustomEvent('dentu:accion', { detail: { accion: 'ir-calendario' } })),
    },
    {
      label: 'Buscar un teléfono',
      icon: Phone,
      roles: ['admin', 'recepcion', 'doctor'],
      run: () => window.dispatchEvent(new CustomEvent('dentu:accion', { detail: { accion: 'ir-pacientes' } })),
    },
  ];
  return base.filter((a) => a.roles.includes(rol || 'doctor'));
};

export const CommandHintIcon = Search;
export { dinero };
